import find from 'lodash/find';
import round from 'lodash/round';
import orderBy from 'lodash/orderBy';

import { NormalizedMatchData } from '../data_types/types';
import { calculateStrokeDiff, getHeadToHeadResult } from './transformers/match_data';

/**
 * Minimum rounds together before someone can be your nemesis, favourite or
 * rival. Matches the "min. 3 rounds" rule the best/worst records use.
 */
export const MIN_RIVAL_ROUNDS = 3;

export type Meeting = {
    timestamp: number;
    result: 'w' | 't' | 'l';
    myScore: number;
    theirScore: number;
    strokeDiff: number;   // positive = you were better
    cardSize: number;
};

export type Rivalry = {
    pID: string;
    name: string;
    icon: number;
    iconBorder: number;
    played: number;
    w: number;
    t: number;
    l: number;
    winPercent: number;
    tiePercent: number;
    lossPercent: number;
    strokeDiff: number;
    strokesPerRound: number;
    lastPlayed: number;
    meetings: Meeting[];  // oldest -> newest
};

export const buildRivalries = (myPID: string, matchData: NormalizedMatchData[]): Rivalry[] => {
    const byOpponent: Record<string, { name: string; icon: number; iconBorder: number; meetings: Meeting[] }> = {};

    orderBy(matchData, 'timestamp', 'asc').forEach(match => {
        const me = find(match.playerData, ['pID', myPID]);
        if (!me) return;

        match.playerData.forEach(opp => {
            if (opp.pID === myPID) return;
            const entry = byOpponent[opp.pID] ?? (byOpponent[opp.pID] = { name: opp.name, icon: 0, iconBorder: 0, meetings: [] });
            // keep the most recent display name and icon
            entry.name = opp.name;
            entry.icon = opp.icon;
            entry.iconBorder = opp.iconBorder;
            entry.meetings.push({
                timestamp: match.timestamp,
                result: getHeadToHeadResult(me, opp),
                myScore: me.totalScore,
                theirScore: opp.totalScore,
                strokeDiff: calculateStrokeDiff(me, opp),
                cardSize: match.cardSize,
            });
        });
    });

    return Object.entries(byOpponent).map(([pID, { name, icon, iconBorder, meetings }]) => {
        const played = meetings.length;
        const count = (r: Meeting['result']) => meetings.filter(m => m.result === r).length;
        const pct = (n: number) => (played ? round((n / played) * 100) : 0);
        const w = count('w');
        const t = count('t');
        const l = count('l');
        const strokeDiff = meetings.reduce((sum, m) => sum + m.strokeDiff, 0);
        return {
            pID,
            name,
            icon,
            iconBorder,
            played,
            w,
            t,
            l,
            winPercent: pct(w),
            tiePercent: pct(t),
            lossPercent: pct(l),
            strokeDiff,
            strokesPerRound: played ? round(strokeDiff / played, 1) : 0,
            lastPlayed: meetings[played - 1]?.timestamp ?? 0,
            meetings,
        };
    });
};

const qualified = (rivalries: Rivalry[]) => rivalries.filter(r => r.played >= MIN_RIVAL_ROUNDS);

/** Highest loss rate; more rounds and worse strokes break ties. */
export const findNemesis = (rivalries: Rivalry[]) =>
    orderBy(qualified(rivalries).filter(r => r.l > r.w), ['lossPercent', 'played', 'strokesPerRound'], ['desc', 'desc', 'asc'])[0];

/** Highest win rate; more rounds and better strokes break ties. */
export const findFavourite = (rivalries: Rivalry[]) =>
    orderBy(qualified(rivalries).filter(r => r.w > r.l), ['winPercent', 'played', 'strokesPerRound'], ['desc', 'desc', 'desc'])[0];

/**
 * The closest head to head among your most played opponents: the rivalry
 * that could go either way.
 */
export const findRival = (rivalries: Rivalry[], exclude: (string | undefined)[]) =>
    orderBy(
        qualified(rivalries).filter(r => !exclude.includes(r.pID)),
        [r => Math.abs(r.w - r.l) / r.played, 'played'],
        ['asc', 'desc'],
    )[0];

export const initialsOf = (name: string) =>
    (name || '?')
        .split(/[\s_]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');
